/** Сообщить о проблеме и своя история.
 *
 * Багрепорты писались в базу вместе с контекстом последних записей — именно так
 * нашёлся баг с годовыми подписками, — но прочитать их можно было только руками
 * из базы. Человек не знал, услышали ли его.
 */
import { useEffect, useState } from "react";
import { ApiError, api, type BugReport, type Failure } from "../lib/api.ts";
import { tap } from "../lib/telegram.ts";
import { Header } from "../components/Header.tsx";
import { Fallback } from "./Fallback.tsx";
import { card, hint } from "../components/ui.tsx";

export function BugsScreen({ onBack }: { onBack: () => void }) {
  const [rows, setRows] = useState<BugReport[] | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");

  const load = () => {
    setFailure(null);
    api.bugs().then(setRows)
      .catch((e) => setFailure(e instanceof ApiError ? e.kind : "broken"));
  };

  useEffect(load, []);

  if (failure) return <Fallback kind={failure} onRetry={load} />;

  return (
    <div style={{ padding: "var(--screen)" }}>
      <Header title="Сообщить о проблеме" onBack={onBack} />

      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000}
        placeholder="Что пошло не так? Пиши как есть — приложу последние записи, разбираться буду по ним."
        aria-label="Что пошло не так"
        style={{ width: "100%", background: "var(--surface)", border: "none",
                 borderRadius: "var(--r-card)", padding: "14px 16px", color: "var(--text)",
                 font: "inherit", fontSize: "var(--f-body)", resize: "none" }} />
      {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}
      <button type="button" disabled={!note.trim() || busy}
        onClick={() => {
          tap();
          setBusy(true);
          setTrouble("");
          api.reportBug(note.trim())
            .then((next) => { setRows(next); setNote(""); })
            .catch(() => setTrouble("Не получилось отправить. Попробуй ещё раз."))
            .finally(() => setBusy(false));
        }}
        style={{ width: "100%", minHeight: "var(--tap)", marginTop: "var(--s-3)", padding: 14,
                 borderRadius: "var(--r-field)", border: "none", cursor: "pointer",
                 font: "inherit", fontSize: "var(--f-body)", fontWeight: 600,
                 background: note.trim() && !busy ? "var(--accent)" : "var(--surface-2)",
                 color: note.trim() && !busy ? "var(--on-accent)" : "var(--text-disabled)" }}>
        Отправить
      </button>

      {rows !== null && rows.length > 0 && (
        <>
          <h2 style={{ fontSize: "var(--f-t3)", fontWeight: 600,
                       margin: "var(--s-6) 0 var(--s-3)" }}>
            Что я уже присылал
          </h2>
          <section style={{ ...card, padding: "2px var(--s-5)" }}>
            {rows.map((bug, i) => (
              <div key={bug.id} style={{ padding: "13px 0",
                                         borderBottom: i === rows.length - 1
                                           ? "none" : "1px solid var(--separator)" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--s-3)" }}>
                  <span style={{ flexGrow: 1, fontSize: "var(--f-body-s)", lineHeight: 1.5 }}>
                    {bug.note}
                  </span>
                  <span style={{ fontSize: "var(--f-micro)", whiteSpace: "nowrap",
                                 borderRadius: 5, padding: "2px 6px",
                                 color: bug.resolved ? "var(--income)" : "var(--warn)",
                                 background: bug.resolved
                                   ? "color-mix(in srgb, var(--income) 12%, transparent)"
                                   : "color-mix(in srgb, var(--warn) 12%, transparent)" }}>
                    {bug.resolved ? "починено" : "смотрю"}
                  </span>
                </div>
                <div style={{ fontSize: "var(--f-caption-s)", color: "var(--text-faint)",
                              marginTop: 6 }}>
                  {formatDay(bug.created_at)}
                </div>
              </div>
            ))}
          </section>
        </>
      )}
    </div>
  );
}

const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня",
                "июля", "августа", "сентября", "октября", "ноября", "декабря"];

function formatDay(iso: string): string {
  const [, month, day] = iso.slice(0, 10).split("-");
  return `${Number(day)} ${MONTHS[Number(month) - 1]}`;
}

